
import React, { useState } from 'react';
import { Client, Lead } from '../types';
import { Icons } from '../constants';

interface ClientsPageProps {
  leads: Lead[];
  onUpdateLeadStatus: (id: string, status: Lead['status']) => void;
}

export const Clients: React.FC<ClientsPageProps> = ({ leads, onUpdateLeadStatus }) => {
  const [activeTab, setActiveTab] = useState<'clients' | 'leads'>('leads');
  
  const [clients, setClients] = useState<Client[]>([
    { id: '1', name: 'Alice Johnson', email: 'alice@startup.co', company: 'Nova Tech', status: 'Active', websiteId: 'site-1' },
    { id: '2', name: 'Bob Smith', email: 'bob@design.io', company: 'Skyline Studio', status: 'Active', websiteId: 'site-2' },
  ]);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CRM & Leads Dashboard</h1>
          <p className="text-slate-500">Manage your business clients and track promotional redemptions.</p>
        </div>
        <button className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all flex items-center gap-2">
          <Icons.Plus /> Add Client
        </button>
      </div>

      <div className="flex border-b border-slate-200 gap-8">
         <button 
           onClick={() => setActiveTab('leads')}
           className={`pb-4 text-sm font-bold transition-all relative ${activeTab === 'leads' ? 'text-indigo-600' : 'text-slate-400'}`}
         >
           Leads & Redemptions
           <span className="ml-2 bg-indigo-50 text-indigo-600 text-[10px] px-2 py-0.5 rounded-full">{leads.length}</span>
           {activeTab === 'leads' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600 rounded-full"></div>}
         </button>
         <button 
           onClick={() => setActiveTab('clients')}
           className={`pb-4 text-sm font-bold transition-all relative ${activeTab === 'clients' ? 'text-indigo-600' : 'text-slate-400'}`}
         >
           Tenant Clients
           {activeTab === 'clients' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600 rounded-full"></div>}
         </button>
      </div>

      {activeTab === 'leads' ? (
        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Subscriber Email</th>
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Promotion Code (ID)</th>
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Campaign Name</th>
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Redeem Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center text-slate-400 italic">No promotion sign-ups yet.</td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-8 py-5">
                       <p className="font-bold text-slate-900">{lead.email}</p>
                       <p className="text-[10px] text-slate-400 mt-1">{new Date(lead.timestamp).toLocaleString()}</p>
                    </td>
                    <td className="px-8 py-5">
                      <span className="bg-indigo-50 text-indigo-700 text-xs font-mono px-2 py-1 rounded-md font-bold">
                        {lead.promoId}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <p className="text-sm font-medium text-slate-600">{lead.promoTitle}</p>
                    </td>
                    <td className="px-8 py-5 text-right">
                       <button 
                        onClick={() => onUpdateLeadStatus(lead.id, lead.status === 'New' ? 'Redeemed' : 'New')}
                        className={`text-[10px] font-bold uppercase px-3 py-1.5 rounded-full transition-all ${
                          lead.status === 'Redeemed' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-100'
                        }`}
                       >
                         {lead.status === 'New' ? 'Mark Redeemed' : 'Redeemed ✓'}
                       </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client Name</th>
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Company</th>
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Website</th>
                <th className="px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-slate-50/30 transition-colors">
                  <td className="px-8 py-5 font-bold text-slate-900">{client.name}</td>
                  <td className="px-8 py-5 text-sm text-slate-600">{client.company}</td>
                  <td className="px-8 py-5 text-xs font-bold text-indigo-600 uppercase tracking-tighter">#{client.websiteId}</td>
                  <td className="px-8 py-5 text-right flex justify-end gap-2">
                    <button className="p-2 text-slate-400 hover:text-indigo-600"><Icons.Edit /></button>
                    <button className="p-2 text-slate-400 hover:text-rose-600"><Icons.Trash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
